import { AlertTriangle } from 'lucide-react';
import { Link, generatePath } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { ROUTES } from '../../../constants/routes';
import type { LowStockAlert } from '../../../hooks/inventory/useInventoryDashboard';

interface LowStockListProps {
  data: LowStockAlert[];
  className?: string;
}

function formatVariant(a: LowStockAlert) {
  return [a.product_name, a.size, a.color].filter(Boolean).join(' · ');
}

export function LowStockList({ data, className = '' }: LowStockListProps) {
  return (
    <Card className={`bg-[#221F1F] border-[#504A4A] ${className}`}>
      <CardHeader>
        <CardTitle className="text-text-primary flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          Low-stock alerts
          {data.length > 0 && (
            <span className="text-xs text-white/60 font-normal">
              ({data.length} variant{data.length === 1 ? '' : 's'})
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="text-text-secondary text-sm py-6 text-center">
            Nothing actively selling is below threshold — you're good.
          </p>
        ) : (
          <div className="divide-y divide-[#363232]">
            <div className="grid grid-cols-[1fr_80px_80px_120px] gap-3 pb-2 text-xs font-semibold text-white/60 uppercase">
              <div>Variant</div>
              <div className="text-right">On hand</div>
              <div className="text-right">Days left</div>
              <div className="text-right">Reorder</div>
            </div>
            {data.map((a) => {
              const urgent = a.days_of_cover !== null && a.days_of_cover < 3;
              return (
                <Link
                  key={a.variant_id}
                  to={generatePath(ROUTES.INVENTORY_VARIANT_DETAIL, { variantId: a.variant_id })}
                  className="grid grid-cols-[1fr_80px_80px_120px] gap-3 py-3 text-sm hover:bg-[#2a2727] -mx-2 px-2 rounded transition-colors"
                >
                  <div className="min-w-0">
                    <div className="text-text-primary truncate">{formatVariant(a)}</div>
                    <div className="text-xs text-white/50 truncate">SKU {a.sku}</div>
                  </div>
                  <div className={`text-right font-mono ${urgent ? 'text-red-400' : 'text-text-primary'}`}>
                    {a.quantity}
                  </div>
                  <div className={`text-right font-mono ${urgent ? 'text-red-400' : 'text-text-primary'}`}>
                    {a.days_of_cover !== null ? a.days_of_cover.toFixed(1) : '—'}
                  </div>
                  <div className="text-right font-mono text-text-primary">
                    {a.recommended_reorder_qty > 0 ? `+${a.recommended_reorder_qty}` : '—'}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
